import { z } from "zod"
import {
  AlternativeSchema,
  CreateGlossaryReqSchema,
  FeedbackSchema,
  FileQueueSchema,
  FileUrlSchema,
  GlossarySchema,
  GlossarySnakeCaseSchema,
  MachineTranslationFormSchema,
  MediaTranslationResultSchema,
  MtDocumentJobSchema,
  MtFileSchema,
  MtTextJobSchema,
  PredictCostRequestSchema,
  PredictCostResponseSchema,
  presignedUrlSchema,
  SmartGlossarySchema,
  SubtitleSchema,
  TextTranslationProgressSchema,
  TranscriptItemSchema,
  TranslationSearchSchema,
  TranslationSegmentSchema,
  UpdateSmartGlossaryReqSchema,
} from "./translation.schema"

export enum TranslationTypeEnum {
  Mtpe = "mtpe",
  Mt = "mt",
  Ht = "ht",
}

export enum TranslationModeEnum {
  Text = "text",
  File = "file",
  ExpertReviews = "expert",
  Media = "media",
}

export enum SelectModeEnum {
  SourceLanguage = "sourceLanguage",
  TargetLanguage = "targetLanguage",
  Domain = "domain",
}

export enum TranslationEngineEnum {
  BeringAI = "bering_ai",
  DeepL = "deepl",
}

// Domain identities are managed by the service catalogue; these names preserve existing callers.
export const DomainTypeEnum = {
  Legal: "legal", Patent: "patent", General: "general", Business: "business",
  Medical: "medical", Kakao: "kakao",
} as const
export type DomainTypeEnum = string

export enum ExpertDomainTypeEnum {
  Legal = "legal",
  Patent = "patent",
  General = "general",
  Business = "business",
  Academic = "academic",
}

export enum PatentTranslateLanguageTypeEnum {
  Auto = "auto",
  Eng = "en",
  Kor = "ko",
  Jpn = "ja",
  Cmn = "zh",
  Zho = "zh-hant",
  Ind = "id",
  Fra = "fr",
  Spa = "es",
  Deu = "de",
  Ara = "ar",
  Vie = "vi",
}

export enum LegalTranslateLanguageTypeEnum {
  Auto = "auto",
  Eng = "en",
  Kor = "ko",
  Jpn = "ja",
  Cmn = "zh",
  Zho = "zh-hant",
  Ind = "id",
  Fra = "fr",
  Spa = "es",
  Deu = "de",
  Ara = "ar",
  Vie = "vi",
  Por = "pt",
  Mya = "my",
  Khm = "km",
  Tha = "th",
  Mon = "mn",
  Msa = "ms",
}

export enum KakaoTranslateLanguageTypeEnum {
  Auto = "auto",
  Eng = "en",
  Kor = "ko",
}

export enum TranslateLanguageTypeEnum {
  Auto = "auto",
  Zh = "zh",
  En = "en",
  Fr = "fr",
  Pt = "pt",
  Es = "es",
  Ja = "ja",
  Tr = "tr",
  Ru = "ru",
  Ar = "ar",
  Ko = "ko",
  Th = "th",
  It = "it",
  De = "de",
  Vi = "vi",
  Ms = "ms",
  Id = "id",
  Tl = "tl",
  Hi = "hi",
  ZhHant = "zh-hant",
  Pl = "pl",
  Cs = "cs",
  Nl = "nl",
  Km = "km",
  My = "my",
  Fa = "fa",
  Gu = "gu",
  Ur = "ur",
  Te = "te",
  Mr = "mr",
  He = "he",
  Bn = "bn",
  Ta = "ta",
  Uk = "uk",
  Bo = "bo",
  Kk = "kk",
  Mn = "mn",
  Ug = "ug",
  Yue = "yue",
}

export enum EngEnum {
  Eng = "eng",
}

export enum TranslationStatusEnum {
  Ready = "ready",
  InProgress = "in_progress",
  Completed = "completed",
  Failed = "failed", // 작업 처리 과정 중 실패
  TimedOut = "timed_out", // 작업 시간 초과
  NotFoundOrUnauthorized = "not_found_or_unauthorized", // 작업을 찾을 수 없거나, 권한이 없음
  Error = "error", // 인프라 에러
  ClientTimeout = "client_timeout", // 클라이언트 타임아웃 (연결실패)
}

export enum QuotaStatusEnum {
  QuotaExceeded = "quota_exceeded",
  WithinQuota = "within_quota",
}

export type Glossary = z.infer<typeof GlossarySchema>
export type GlossarySnakecase = z.infer<typeof GlossarySnakeCaseSchema>
export type CreateGlossaryReq = z.infer<typeof CreateGlossaryReqSchema>
export type MachineTranslationForm = z.infer<
  typeof MachineTranslationFormSchema
>
export type FileQueue = z.infer<typeof FileQueueSchema>
export type TextTranslationProgress = z.infer<
  typeof TextTranslationProgressSchema
>
export type MtTextJob = z.infer<typeof MtTextJobSchema>
export type MtDocumentJob = z.infer<typeof MtDocumentJobSchema>

// Response shape of GET /mt/documents/{id}/versions. Distinct from
// MtDocumentJob because the lineage endpoint omits source_title / input_path
// / job_id and uses `id` as the canonical identifier.
export interface DocumentVersionInfo {
  id: string
  version: number
  lineageId: string
  parentJobId: string | null
  status: TranslationStatusEnum
  createDt: string
  sourceLanguage: TranslateLanguageTypeEnum
  targetLanguage: TranslateLanguageTypeEnum
  domain: DomainTypeEnum
  engine?: TranslationEngineEnum
}
export type MtFile = z.infer<typeof MtFileSchema>
export type PresignedUrlType = z.infer<typeof presignedUrlSchema>
export type FileUrl = z.infer<typeof FileUrlSchema>
export type FeedbackForm = z.infer<typeof FeedbackSchema>
export type TranslationSearchType = z.infer<typeof TranslationSearchSchema>
export type TranslationSegment = z.infer<typeof TranslationSegmentSchema>
export type Alternative = z.infer<typeof AlternativeSchema>
export type SmartGlossary = z.infer<typeof SmartGlossarySchema>
export type UpdateSmartGlossaryReq = z.infer<
  typeof UpdateSmartGlossaryReqSchema
>
export type PredictCostRequest = z.infer<typeof PredictCostRequestSchema>
export type PredictCostResponse = z.infer<typeof PredictCostResponseSchema>
export type Subtitle = z.infer<typeof SubtitleSchema>
export type TranscriptItem = z.infer<typeof TranscriptItemSchema>
export type MediaTranslationResult = z.infer<
  typeof MediaTranslationResultSchema
>

export type CATInlineCodeKind = "standalone" | "paired_start" | "paired_end"

export type CATInlineCodeRole =
  | "run_boundary"
  | "style_boundary"
  | "formatting"
  | "placeholder"
  | "note"
  | "field"
  | "other"

export interface CATInlineCode {
  marker: string
  codeId: string
  kind: CATInlineCodeKind
  pairId?: string | null
  role: CATInlineCodeRole
  displayName?: string | null
  canCopy: boolean
  canDelete: boolean
  canReorder: boolean
}

export interface CATSegment {
  id: string
  sequenceNumber: number
  originalText: string
  taggedOriginalText?: string | null
  sourceInlineTags?: string[]
  sourceInlineCodes?: CATInlineCode[]
  displayTaggedOriginalText?: string | null
  displaySourceInlineTags?: string[]
  displaySourceInlineCodes?: CATInlineCode[]
  translatedText: string | null
  revision: number
  taggedTranslatedText?: string | null
  inlineTags?: string[]
  inlineCodes?: CATInlineCode[]
  // Populated only when the request used compare_with_previous=true AND
  // the job has a parent revision. Alignment is index-based by sequenceNumber.
  previousOriginalText?: string | null
  previousTranslatedText?: string | null
  originalChanged?: boolean | null
  translationChanged?: boolean | null
  review?: CATSegmentReview
}

export type CATEditReason =
  | "manual"
  | "autosave"
  | "undo"
  | "redo"
  | "replace"
  | "confirm"
  | "import"
  | "review_proposal"

export type CATXliffExportFormat = "xliff_1_2" | "xliff_2_0"

export type CATXliffExportStatus = "queued" | "completed" | "failed"

export interface CATXliffExportArtifact {
  fileName: string
  mediaType: string
  fileSize: number
  checksum: string
}

export interface CATXliffExportRecord {
  id: string
  jobId: string
  format: CATXliffExportFormat
  profile: string
  status: CATXliffExportStatus
  segmentCount: number
  segmentRevisionDigest: string
  documentVersionId?: string | null
  sourceRevision?: number | null
  artifact?: CATXliffExportArtifact | null
  errorMessage?: string | null
  createDt: string
  updateDt: string
  expiresDt: string
  completedDt?: string | null
}

export interface CATXliffExportResponse {
  success: boolean
  export: CATXliffExportRecord
}

export interface CATXliffExportDownloadResponse {
  success: boolean
  exportId: string
  fileName: string
  fileSize: number
  checksum: string
  downloadUrl: string
}

export type CATXliffImportStatus =
  | "queued"
  | "ready"
  | "applying"
  | "applied"
  | "failed"

export type CATXliffImportConflictKind =
  | "job_mismatch"
  | "document_version_mismatch"
  | "language_mismatch"
  | "snapshot_mismatch"
  | "unknown_segment"
  | "missing_segment"
  | "duplicate_segment"
  | "source_revision_mismatch"
  | "segment_revision_mismatch"
  | "source_hash_mismatch"
  | "target_hash_mismatch"
  | "source_content_mismatch"
  | "protected_code_mismatch"
  | "locked_segment"

export interface CATXliffImportReport {
  format: CATXliffExportFormat
  unitCount: number
  changedCount: number
  canApply: boolean
  conflictsTruncated: boolean
  conflicts: Array<{
    kind: CATXliffImportConflictKind
    segmentId?: string | null
    unitId?: string | null
    unitName?: string | null
    sequenceNumber?: number | null
    sourcePreview?: string | null
    targetPreview?: string | null
  }>
  warnings: string[]
}

export interface CATXliffImportRecord {
  id: string
  jobId: string
  fileName: string
  mediaType: string
  fileSize: number
  checksum: string
  profile: string
  format?: CATXliffExportFormat | null
  status: CATXliffImportStatus
  report?: CATXliffImportReport | null
  appliedCount?: number | null
  errorMessage?: string | null
  createDt: string
  updateDt: string
  expiresDt: string
  completedDt?: string | null
}

export interface CATXliffImportResponse {
  success: boolean
  import: CATXliffImportRecord
}

export interface CATSegmentEditHistoryEntry {
  segmentId: string
  revision: number
  actorId: string
  actorName?: string | null
  actorEmail?: string | null
  reason: CATEditReason
  translatedText: string
  createdAt: string
}

export interface CATSegmentEditHistoryResponse {
  success: boolean
  entries: CATSegmentEditHistoryEntry[]
  nextBeforeRevision?: number | null
}

export type CATSegmentReviewStatus =
  | "untranslated"
  | "translated"
  | "confirmed"
  | "reviewed"
  | "rejected"

export interface CATSegmentReview {
  status: CATSegmentReviewStatus
  needsReconfirmation?: boolean
  reviewerId?: string | null
  reviewerName?: string | null
  commentCount: number
  unresolvedCommentCount?: number
  locked: boolean
  lockedBy?: string | null
  lockedByName?: string | null
}

export interface CATReviewProgress {
  total: number
  untranslated: number
  translated: number
  confirmed: number
  reviewed: number
  rejected: number
  locked: number
  unresolvedComments?: number
}

export type CATWorkflowRole =
  | "project_manager"
  | "translator"
  | "reviewer"
  | "client_reviewer"

export type CATWorkflowPhase =
  | "translation"
  | "review"
  | "client_review"
  | "ready_to_complete"
  | "completed"

/**
 * 초대에 대한 답. `pending`은 담당자로 지명만 된 상태이고, 작업영역 접근 권한이 없다.
 * `declined`는 행을 지우는 대신 남겨 두어 "거절"과 "해제"를 구분한다.
 */
export type CATInvitationState = "pending" | "accepted" | "declined"

export interface CATWorkflowAssignment {
  accountId: string
  accountName: string
  accountEmail?: string | null
  role: CATWorkflowRole
  invitationState: CATInvitationState
}

export interface CATAssignedWorkspace {
  jobId: string
  sourceTitle: string
  sourceLanguage: string
  targetLanguage: string
  role: CATWorkflowRole
  invitationState: CATInvitationState
  assignedAt: string
  updatedAt: string
  completedAt?: string | null
}

export interface CATAssignedWorkspacePage {
  items: CATAssignedWorkspace[]
  total: number
}

export interface CATJobWorkflow {
  enabled: boolean
  currentRole: CATWorkflowRole
  phase: CATWorkflowPhase
  requiredFinalStatus: CATSegmentReviewStatus
  canManage: boolean
  canEdit: boolean
  canReview: boolean
  canClientReview: boolean
  canComplete: boolean
  canResolveComments?: boolean
  canReopen: boolean
  assignments: CATWorkflowAssignment[]
  completedAt?: string | null
}

export interface CATDocumentInfo {
  sourceTitle: string
  inputPath: string
  engine?: string | null
}

export interface CATSegmentComment {
  id: string
  segmentId: string
  parentCommentId?: string | null
  authorId: string
  authorName: string
  authorEmail?: string | null
  body: string
  mentions?: CATSegmentCommentMention[]
  resolvedAt?: string | null
  resolvedBy?: string | null
  createdAt: string
}

export interface CATSegmentCommentMention {
  accountId: string
  accountName: string
}

export interface CATWorkspace {
  jobId: string
  sourceLanguage: string
  targetLanguage: string
  segments: Record<string, CATSegment>
  reviewProgress?: CATReviewProgress
  documentInfo?: CATDocumentInfo | null
  workflow?: CATJobWorkflow | null
  /**
   * Capabilities the server turns on per deployment (env flags, default off). Absent on servers
   * that predate them, which reads the same as off. `terminologyPolicy` is wire
   * `terminology_policy`: the glossary binding endpoint is served.
   */
  features?: CATWorkspaceFeatures
}

export interface CATWorkspaceFeatures {
  references?: boolean
  terminologyPolicy?: boolean
}

export interface CATWorkflowResponse {
  success: boolean
  workflow: CATJobWorkflow
}

export interface CATWorkflowInvitationResponse {
  success: boolean
  invitation: {
    jobId: string
    role: CATWorkflowRole
    invitationState: CATInvitationState
  }
}

export interface CATSegmentReviewUpdateResponse {
  success: boolean
  segmentId: string
  review: CATSegmentReview
  reviewProgress: CATReviewProgress
}

export interface CATSegmentCommentsResponse {
  success: boolean
  comments: CATSegmentComment[]
}

export interface CATSegmentCommentAddResponse {
  success: boolean
  comment: CATSegmentComment
}

export interface CATWorkspaceResponse {
  success: boolean
  data: CATWorkspace | null
  error?: string | null
}

export type AssemblyState = "pending" | "in_progress" | "completed" | "failed"

export interface AssembledFile {
  fileName: string
  filePath: string
  downloadUrl?: string
  fileSize: number
  checksum?: string
}

export interface AssemblyStatus {
  jobId: string
  assemblyRequestId?: string
  state: AssemblyState
  progress: number
  assembledFiles: AssembledFile[]
  completedAt: string | null
  errorMessage: string | null
}

export interface AssemblyStatusResponse {
  success: boolean
  status: AssemblyStatus
}

export interface DocumentAssemblyResponse {
  success: boolean
  message: string
  requestId: string
}

export interface CATSegmentQE {
  segmentId: string
  qeIndex: number
  // 0–100 (f64) per backend.
  score: number
}

export type CATQEUnavailableReason =
  | "projection_state_missing"
  | "projection_disabled"
  | "system_score_missing"

export type CATSegmentQEResponse =
  | {
      success: boolean
      availability: "available"
      systemScore: number
      segments: CATSegmentQE[]
      reason: null
    }
  | {
      success: boolean
      availability: "unavailable"
      systemScore: null
      segments: CATSegmentQE[]
      reason: CATQEUnavailableReason
    }

export type CATQAIssueSeverity = "warning" | "error"

export type CATQAIssueCode =
  | "empty_target"
  | "inline_tag_mismatch"
  | "missing_number"
  | "extra_number"
  | "source_target_identical"
  | "multiple_spaces"
  | "trailing_punctuation_mismatch"
  | "inconsistent_translation"
  | "missing_preferred_term"
  | "forbidden_term"
  | "untranslated_source_text"

export interface CATQAIssue {
  segmentId: string
  fingerprint: string
  code: CATQAIssueCode
  severity: CATQAIssueSeverity
  expected?: string[]
  actual?: string[]
}

export type CATQAFeedbackReason =
  | "false_positive"
  | "intentional_translation"
  | "source_issue"
  | "not_applicable"
  | "other"

export interface CATQAResponse {
  success: boolean
  totalSegments: number
  errorCount: number
  warningCount: number
  ignoredCount: number
  issues: CATQAIssue[]
  ignoredIssues: CATQAIssue[]
}

export type CATQARunState = "current" | "stale"

export interface CATQARunIssue extends CATQAIssue {
  segmentNumber: number
  sourceExcerpt: string
  omittedCount: number
}

export interface CATQARun {
  id: string
  jobId: string
  createdBy: string
  profileKey: string
  profileVersion: number
  ruleVersion: number
  inputFingerprint: string
  resourceFingerprint: string
  segmentRevisions: Record<string, number>
  state: CATQARunState
  sourceTitle?: string
  totalSegments: number
  errorCount: number
  warningCount: number
  ignoredCount: number
  issues: CATQARunIssue[]
  ignoredIssues: CATQARunIssue[]
  createdAt: string
}

export interface CATQARunResponse {
  success: boolean
  feedbackProjectionSupported: boolean
  run?: CATQARun
}

export enum TranslationPhaseEnum {
  Parsing = "parsing",
  Translating = "translating",
  Assembling = "assembling",
  Completed = "completed",
  Failed = "failed",
}

export interface DocumentTranslationProgress {
  success: boolean
  phase: TranslationPhaseEnum
  total: number
  translated: number
  progress: number
}

export interface TranslationFormType {
  translationForm: MachineTranslationForm
  setTranslationForm: (
    field: keyof MachineTranslationForm,
    value: string | DomainTypeEnum | TranslateLanguageTypeEnum
  ) => void
}
